import type {
  HarnessGoalContract,
  HarnessStory,
  HarnessStoryDecision,
  HarnessStoryDependency,
  HarnessStoryPlanSnapshot,
  HarnessStoryQuestion,
  HarnessStoryResource,
  HarnessStoryState,
  StoryCollection,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import { asNonBlankString, isRecord } from '@flowx/designer/modules/ai-builder/utils/payload.util'

const STORY_STATES: Readonly<Record<HarnessStoryState, true>> = {
  not_started: true,
  implementing: true,
  implemented: true,
  needs_you: true,
  stopped: true,
}

/** The explicit shape survives an absent goal and absent/malformed stories. No item fallback. */
export function planUsesStories(payload: unknown): boolean {
  return (
    isRecord(payload) &&
    (payload['expansion'] === 'milestones' || Object.hasOwn(payload, 'stories'))
  )
}

export function toStoryPlanSnapshot(payload: unknown): HarnessStoryPlanSnapshot {
  const source = isRecord(payload) ? payload : {}
  const malformedSnapshot = payload !== undefined && !isRecord(payload)
  const contract = toGoalContract(source['contract'])
  return {
    expansion: asNonBlankString(source['expansion']),
    goal: asNonBlankString(source['goal']) ?? contract?.goal ?? null,
    stories: malformedSnapshot
      ? { availability: 'invalid', entries: [] }
      : uniqueCollection(readCollection(source['stories'], toStory), (story) => story.story_id),
    contract,
    contractAvailability: malformedSnapshot
      ? 'invalid'
      : source['contract'] === undefined
        ? 'missing'
        : contract
          ? 'available'
          : 'invalid',
    decisions: malformedSnapshot
      ? { availability: 'invalid', entries: [] }
      : readCollection(source['decisions'], toStoryDecision),
  }
}

function readCollection<T>(
  payload: unknown,
  read: (entry: unknown, index: number) => T | null,
  complete: (entry: T) => boolean = () => true
): StoryCollection<T> {
  if (!Array.isArray(payload)) {
    return { availability: payload === undefined ? 'missing' : 'invalid', entries: [] }
  }
  const parsed = payload.map(read)
  const entries = parsed.filter((entry): entry is T => entry !== null)
  return {
    availability:
      entries.length === parsed.length && entries.every(complete) ? 'available' : 'invalid',
    entries,
  }
}

function uniqueCollection<T>(
  collection: StoryCollection<T>,
  identity: (entry: T) => string
): StoryCollection<T> {
  const ids = collection.entries.map(identity)
  return new Set(ids).size === ids.length
    ? collection
    : {
        availability: 'invalid',
        // Ambiguous identities cannot be selected or joined safely.
        entries: collection.entries.filter(
          (entry) => ids.filter((id) => id === identity(entry)).length === 1
        ),
      }
}

function strings(payload: unknown): StoryCollection<string> {
  return readCollection(payload, asNonBlankString)
}

function toStory(payload: unknown, index: number): HarnessStory | null {
  if (!isRecord(payload)) {
    return null
  }
  const storyId = asNonBlankString(payload['story_id'])
  if (!storyId) {
    return null
  }
  const resources = isRecord(payload['resources']) ? payload['resources'] : {}
  const unavailableResources = payload['resources'] === undefined ? undefined : null
  const state = asNonBlankString(payload['state'])
  const question = toQuestion(payload['question'])
  return {
    story_id: storyId,
    position: index + 1,
    name: asNonBlankString(payload['name']),
    outcome: asNonBlankString(payload['outcome']),
    state: state && Object.hasOwn(STORY_STATES, state) ? (state as HarnessStoryState) : null,
    forecast: typeof payload['forecast'] === 'boolean' ? payload['forecast'] : null,
    commit_id: asNonBlankString(payload['commit_id']),
    reason: asNonBlankString(payload['reason']),
    question,
    questionAvailability:
      payload['question'] === undefined
        ? 'missing'
        : question &&
            question.question &&
            question.story_id === storyId &&
            question.options.availability === 'available' &&
            question.options.entries.length >= 2 &&
            question.options.entries.length <= 4 &&
            ((isRecord(payload['question']) && payload['question']['recommended'] === null) ||
              (question.recommended !== null &&
                question.options.entries.includes(question.recommended)))
          ? 'available'
          : 'invalid',
    resources: {
      created: resourceCollection(
        isRecord(payload['resources']) ? resources['created'] : unavailableResources
      ),
      edited: resourceCollection(
        isRecord(payload['resources']) ? resources['edited'] : unavailableResources
      ),
      removed: resourceCollection(
        isRecord(payload['resources']) ? resources['removed'] : unavailableResources
      ),
    },
    dependencies: readCollection(
      payload['dependencies'],
      toDependency,
      (dependency) => dependency.names.availability === 'available'
    ),
    decided_while_building: strings(payload['decided_while_building']),
    serves: strings(payload['serves']),
    operations: strings(payload['operations']),
    step_count:
      typeof payload['step_count'] === 'number' &&
      Number.isInteger(payload['step_count']) &&
      payload['step_count'] >= 0
        ? payload['step_count']
        : null,
  }
}

function resourceCollection(payload: unknown): StoryCollection<HarnessStoryResource> {
  return readCollection(
    payload,
    (resource) =>
      isRecord(resource)
        ? { label: asNonBlankString(resource['label']), name: asNonBlankString(resource['name']) }
        : null,
    (resource) => resource.label !== null && resource.name !== null
  )
}

function toDependency(payload: unknown): HarnessStoryDependency | null {
  if (!isRecord(payload)) {
    return null
  }
  const storyId = asNonBlankString(payload['story_id'])
  return storyId ? { story_id: storyId, names: strings(payload['names']) } : null
}

function toQuestion(payload: unknown): HarnessStoryQuestion | null {
  return isRecord(payload)
    ? {
        question: asNonBlankString(payload['question']),
        options: strings(payload['options']),
        recommended: asNonBlankString(payload['recommended']),
        story_id: asNonBlankString(payload['story_id']),
      }
    : null
}

function toStoryDecision(payload: unknown): HarnessStoryDecision | null {
  return isRecord(payload) &&
    (payload['story_id'] == null || asNonBlankString(payload['story_id']) !== null)
    ? {
        story_id: asNonBlankString(payload['story_id']),
        text: asNonBlankString(payload['text']),
        chosen: asNonBlankString(payload['chosen']),
        alternatives: strings(payload['alternatives']),
      }
    : null
}

function toGoalContract(payload: unknown): HarnessGoalContract | null {
  if (!isRecord(payload)) {
    return null
  }
  return {
    goal: asNonBlankString(payload['goal']),
    milestones: uniqueCollection(
      readCollection(payload['milestones'], (entry) => {
        if (!isRecord(entry)) {
          return null
        }
        const id = asNonBlankString(entry['milestone_id'])
        return id ? { milestone_id: id, acceptance: strings(entry['acceptance']) } : null
      }),
      (milestone) => milestone.milestone_id
    ),
    acceptance: uniqueCollection(
      readCollection(
        payload['acceptance'],
        (entry) => {
          if (!isRecord(entry)) {
            return null
          }
          const id = asNonBlankString(entry['acceptance_id'])
          return id ? { acceptance_id: id, statement: asNonBlankString(entry['statement']) } : null
        },
        (entry) => entry.statement !== null
      ),
      (entry) => entry.acceptance_id
    ),
  }
}
